import React, { useState, useCallback } from 'react';
import CpscReview from './CpscReview'; // CHA-267 PR-2 — the shared Review's LINK lane is the link form
import CpscCertLinkRecords from './CpscCertLinkRecords'; // CHA-267 PR-3 — the shared links table (also in Register)

/**
 * CpscCertLinks — CHA-229. Manual style→cert LINK override. When the resolution engine CANNOT auto-match a
 * certificate to a production style (e.g. a Target BV cert keyed by a PID like PID-715PG8 that shares no key
 * with the vendor style ABH40 — only the UPC bridges them, and it's not in the export), an operator LINKS the
 * style to a real cert/report. The style then reads the LINKED cert's REAL status on the dashboard AND the
 * broker. DISTINCT from the "Declare exempt" flow (CHA-225, which WAIVES the cert requirement) and from the
 * ship-without-cert override (CHA-179) — a LINK resolves the style to a real cert so it reads certified.
 *
 * CHA-267 PR-2/PR-3: this surface is now a thin composition — the create form is the shared CpscReview's
 * LINK lane; the list (unlink + engine-resolved honest status) is the shared CpscCertLinkRecords, which
 * Register also mounts. An unlink remounts the form via onUnlinked so a stale "linked" success row never
 * outlives the link. Federal-safe posture unchanged: a link only ever reflects a REAL cert's status.
 */
export default function CpscCertLinks({ userProfile } = {}) {
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner' || role === 'platform_admin';
  const isAdmin = isPlatformRole || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';
  const tenantArg = useCallback((extra = {}) => (isPlatformRole ? { tenantId, ...extra } : { ...extra }), [isPlatformRole, tenantId]);

  const [unlinkCount, setUnlinkCount] = useState(0); // remounts the LINK form after an unlink — a stale "linked" success row must not outlive the link

  if (!isAdmin) return <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">Cert linking is restricted to administrators.</div>;

  return (
    <div className="font-plex text-carbon-ink space-y-4 max-w-4xl">
      <div>
        <h3 className="text-base font-semibold text-carbon-ink">CPSC — Manual Style → Cert Link</h3>
        <p className="text-sm text-carbon-gray-70">Link a production style to a real certificate/report when auto-matching can&apos;t bridge them (e.g. a Target BV cert keyed by a PID that shares no key with the vendor style). The linked style then reads the <strong>linked cert&apos;s actual status</strong> on the dashboard and the broker worklist. If the linked cert isn&apos;t certified yet, the style shows that status; unlinking reverts it to needs-cert.</p>
      </div>

      {isPlatformRole && <div className="text-xs text-carbon-blue-70 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-2">Acting as <strong>{role}</strong> against tenant: <strong>{tenantId}</strong></div>}

      {/* Link form — CHA-267 PR-2: the shared Review's LINK lane (same gates: ≥1 style; Report No and/or
          PID required — a link must reference a REAL cert). */}
      <CpscReview lane="LINK" key={`link-${unlinkCount}`} prefill={{}} tenantArg={tenantArg} />

      {/* Existing links — CHA-267 PR-3: the SHARED links table (also mounted by Register). Unlink +
          engine-resolved status live in CpscCertLinkRecords (one implementation, §8.2); onUnlinked keeps
          the form-remount coupling. */}
      <CpscCertLinkRecords userProfile={userProfile} onUnlinked={() => setUnlinkCount((n) => n + 1)} />
    </div>
  );
}
